import React from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { XIcon, Loader2Icon, EditIcon } from 'lucide-react';
import * as Dialog from '@radix-ui/react-dialog';
import { motion, AnimatePresence } from 'framer-motion';
// Validation Schema
const editClassroomSchema = z.object({
  name: z.string().min(1, 'Classroom name is required'),
  grade: z.string().min(1, 'Grade level is required'),
  section: z.string().optional()
});
type EditClassroomFormValues = z.infer<typeof editClassroomSchema>;
interface EditClassroomModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: EditClassroomFormValues) => Promise<void>;
  classroom: {
    name: string;
    grade: string;
    section?: string;
  };
}
export const EditClassroomModal: React.FC<EditClassroomModalProps> = ({
  isOpen,
  onClose,
  onSave,
  classroom
}) => {
  const {
    register,
    handleSubmit,
    formState: {
      errors,
      isSubmitting
    },
    reset
  } = useForm<EditClassroomFormValues>({
    resolver: zodResolver(editClassroomSchema),
    defaultValues: {
      name: classroom.name,
      grade: classroom.grade,
      section: classroom.section || ''
    }
  });
  const onSubmit = async (data: EditClassroomFormValues) => {
    try {
      await onSave(data);
      reset();
      onClose();
    } catch (error) {
      console.error('Failed to update classroom:', error);
    }
  };
  return <Dialog.Root open={isOpen} onOpenChange={onClose} data-id="element-1291">
      <AnimatePresence data-id="element-1292">
        {isOpen && <Dialog.Portal forceMount container={document.body} data-id="element-1293">
            <Dialog.Overlay asChild data-id="element-1294">
              <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" style={{
            zIndex: 9998
          }} data-id="element-1295">
                <Dialog.Content asChild data-id="element-1296">
                  <motion.div initial={{
                opacity: 0,
                scale: 0.95,
                y: 20
              }} animate={{
                opacity: 1,
                scale: 1,
                y: 0
              }} exit={{
                opacity: 0,
                scale: 0.95,
                y: 20
              }} className="w-full max-w-md max-h-[85vh] bg-white rounded-2xl shadow-xl flex flex-col overflow-hidden relative" style={{
                zIndex: 9999
              }} onClick={e => e.stopPropagation()} data-id="element-1297">
                    {/* Header - Fixed */}
                    <div className="flex items-center justify-between p-6 border-b border-gray-100 bg-gray-50/50 flex-shrink-0" data-id="element-1298">
                      <div className="flex items-center gap-3" data-id="element-1299">
                        <div className="w-10 h-10 rounded-xl bg-[#00B75F]/10 flex items-center justify-center text-[#00B75F]" data-id="element-1300">
                          <EditIcon size={20} data-id="element-1301" />
                        </div>
                        <div data-id="element-1302">
                          <Dialog.Title className="text-lg font-bold text-text-primary" data-id="element-1303">
                            Edit Classroom Details
                          </Dialog.Title>
                          <Dialog.Description className="text-xs text-text-secondary" data-id="element-1304">
                            Update classroom information
                          </Dialog.Description>
                        </div>
                      </div>
                      <button onClick={onClose} className="p-2 text-text-tertiary hover:text-text-primary hover:bg-gray-100 rounded-lg transition-colors" data-id="element-1305">
                        <XIcon size={20} data-id="element-1306" />
                      </button>
                    </div>

                    {/* Form - Scrollable */}
                    <form onSubmit={handleSubmit(onSubmit)} className="flex-1 overflow-y-auto" data-id="element-1307">
                      <div className="p-6 space-y-5" data-id="element-1308">
                        {/* Name Field */}
                        <div className="space-y-1.5" data-id="element-1309">
                          <label className="text-sm font-semibold text-text-primary" data-id="element-1310">
                            Classroom Name{' '}
                            <span className="text-red-500" data-id="element-1311">*</span>
                          </label>
                          <input {...register('name')} placeholder="e.g. Grade 7A Science" className={`w-full px-3 py-2.5 bg-white border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#00B75F]/20 transition-all ${errors.name ? 'border-red-300 focus:border-red-500' : 'border-custom focus:border-[#00B75F]'}`} data-id="element-1312" />
                          {errors.name && <p className="text-xs text-red-500 font-medium" data-id="element-1313">
                              {errors.name.message}
                            </p>}
                        </div>

                        <div className="grid grid-cols-2 gap-4" data-id="element-1314">
                          {/* Grade Field */}
                          <div className="space-y-1.5" data-id="element-1315">
                            <label className="text-sm font-semibold text-text-primary" data-id="element-1316">
                              Grade Level{' '}
                              <span className="text-red-500" data-id="element-1317">*</span>
                            </label>
                            <select {...register('grade')} className={`w-full px-3 py-2.5 bg-white border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#00B75F]/20 transition-all ${errors.grade ? 'border-red-300 focus:border-red-500' : 'border-custom focus:border-[#00B75F]'}`} data-id="element-1318">
                              <option value="" data-id="element-1319">Select Grade</option>
                              {[...Array(12)].map((_, i) => <option key={i} value={`Grade ${i + 1}`} data-id="element-1320">
                                  Grade {i + 1}
                                </option>)}
                              <option value="Kindergarten" data-id="element-1321">Kindergarten</option>
                            </select>
                            {errors.grade && <p className="text-xs text-red-500 font-medium" data-id="element-1322">
                                {errors.grade.message}
                              </p>}
                          </div>

                          {/* Section Field */}
                          <div className="space-y-1.5" data-id="element-1323">
                            <label className="text-sm font-semibold text-text-primary" data-id="element-1324">
                              Section{' '}
                              <span className="text-text-tertiary font-normal" data-id="element-1325">
                                (Optional)
                              </span>
                            </label>
                            <input {...register('section')} placeholder="e.g. A, B, 101" className="w-full px-3 py-2.5 bg-white border border-custom rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#00B75F]/20 focus:border-[#00B75F] transition-all" data-id="element-1326" />
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="pt-4 flex items-center gap-3" data-id="element-1327">
                          <button type="button" onClick={onClose} className="flex-1 px-4 py-2.5 border border-custom rounded-xl text-sm font-semibold text-text-secondary hover:bg-gray-50 transition-colors" data-id="element-1328">
                            Cancel
                          </button>
                          <button type="submit" disabled={isSubmitting} className="flex-1 px-4 py-2.5 bg-[#00B75F] text-white rounded-xl text-sm font-semibold hover:bg-[#00a055] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2" data-id="element-1329">
                            {isSubmitting ? <>
                                <Loader2Icon size={16} className="animate-spin" data-id="element-1330" />
                                Saving...
                              </> : 'Save Changes'}
                          </button>
                        </div>
                      </div>
                    </form>
                  </motion.div>
                </Dialog.Content>
              </motion.div>
            </Dialog.Overlay>
          </Dialog.Portal>}
      </AnimatePresence>
    </Dialog.Root>;
};